var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var OrthographicPlannerCamera_1;
import { IVector2, IVector3, LineType, IBoundingBox, CardinalDirection, IMatrix4, ITransformedBoundingBox } from '../../api.js';
import { EPSILON_DISTANCE, clamp, lerp } from '../../Math.js';
import { OrthographicProjection } from '../OrthographicProjection.js';
import { ViewerCamera } from '../../babylon/camera/ViewerCamera.js';
import { update_from_partial } from '../../babylon/utility/utils.js';
import { Observable } from '../../Observable.js';
import { PrimitiveBuilder } from '../PrimitiveBuilder/PrimitiveBuilder.js';
import { Debug } from '../../utils/DebugLayer/utils/DebugDecorators.js';
import { ORTHOGRAPHIC_CAMERA_DESCRIPTION } from './debug/OrthographicCameraDescription.js';
import { OrthographicCameraVisualizer } from './debug/visualizers/OrthographicCameraVisualizer.js';
import { CameraFrustumVisualizer } from '../../babylon/debug/visualizers/CameraFrustumVisualizer.js';
import { create_visualizers_container } from './debug/visualizers/VisualizersContainer.js';
import { Deprecated } from '../../utils/Deprecated.js';
const DEFAULT_OFFSET = { min: 0.5, max: 40 };
const ALLOWED_OFFSET = { min: 0.1, max: Infinity };
const DEFAULT_PLANE = { near: 0.01, far: 100 };
const DEFAULT_FRUSTUM_VALUE = 2.5;
const MIN_FRUSTUM_VALUE = 0.005; // Minimum height is then 1 centimeter
const INERTIA_SPEED_PAN = 10;
const INERTIA_SPEED_ZOOM = 10;
const LIMIT_SPRING_STIFFNESS_PAN = 5.0;
const LIMIT_SPRING_LERP_MULTIPLIER = 4.0;
function target_lerp(value, target, t, epsilon) {
    if (Math.abs(value - target) <= epsilon)
        return target;
    return lerp(value, target, t);
}
/**
 * The camera uses orthographic projection, which means that all distances in the scene will be consistent on the screen.
 * The camera is interactive and can be panned and zoomed (not rotated). The direction is set via the set_direction function
 * and can be either TOP, LEFT, RIGHT, BACK or FRONT (not BOTTOM)
 * {@link https://i-p-e-x.atlassian.net/wiki/x/_IATgwg | Documentation}
 * {@link https://betrakta.azurewebsites.net/latest/#/new_camera_system/params?descriptions=true&mode=default&codebox=false | Example }
 */
let OrthographicPlannerCamera = OrthographicPlannerCamera_1 = class OrthographicPlannerCamera extends ViewerCamera {
    drag_start(evt) {
        this._is_panning = true;
    }
    drag_end(evt) {
        this._is_panning = false;
    }
    pan_start(evt) {
        this._is_panning = true;
    }
    pan_end(evt) {
        this._is_panning = false;
    }
    constructor(direction, position_on_plane, viewer) {
        super(viewer);
        this._target_position_on_plane = new IVector2(0, 0); // Target position in the screen plane (right and up)
        this._camera_transform_cache = new IMatrix4();
        this._view_matrix_cache = new IMatrix4();
        this._position_on_plane = new IVector2(0, 0); // Target position in the screen plane (right and up)
        this._right = IVector3.copy(ViewerCamera.RIGHT);
        this._up = IVector3.copy(ViewerCamera.UP);
        this._pan_min = new IVector2(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);
        this._pan_max = new IVector2(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
        this._offset_min = DEFAULT_OFFSET.min;
        this._offset_max = DEFAULT_OFFSET.max;
        this._is_dirty = true;
        this._notify_observers = false;
        this._aspect_ratio = 1;
        this._previous_update_frame = -1;
        this.plane_position_change_observable = new Observable();
        this._scene_bounds_changed_observer = null;
        this.name = 'OrthographicPlannerCamera';
        this._debug_geometry = null;
        this._pan_input = new IVector2(0, 0);
        this._is_panning = false;
        // When a new object has been added or an object moved that changes the scene bounds.
        // We make sure to update our near and far to always see everything in front of the camera
        this.on_scene_bounds_changed = (_, scene_bounds) => {
            this._scene_bounds = scene_bounds;
            this.is_dirty = true;
        };
        this._projection = new OrthographicProjection(-DEFAULT_FRUSTUM_VALUE * this._aspect_ratio, DEFAULT_FRUSTUM_VALUE * this._aspect_ratio, -DEFAULT_FRUSTUM_VALUE, DEFAULT_FRUSTUM_VALUE, DEFAULT_PLANE.near, DEFAULT_PLANE.far);
        this._target_projection_left = this._projection.left;
        this._target_projection_right = this._projection.right;
        this._target_projection_top = this._projection.top;
        this._target_projection_bottom = this._projection.bottom;
        IVector2.copy_to_ref(position_on_plane, this._position_on_plane);
        IVector2.copy_to_ref(this._position_on_plane, this._target_position_on_plane);
        this._scene_bounds = viewer.get_scene_bounds();
        this.set_cardinal_direction_internal(direction);
    }
    get _pan_sensitivity_x() {
        return Math.abs(this._target_projection_left) + Math.abs(this._target_projection_right);
    }
    get _pan_sensitivity_y() {
        return Math.abs(this._target_projection_top) + Math.abs(this._target_projection_bottom);
    }
    update_aspect_ratio(aspect_ratio) {
        if (this._aspect_ratio === aspect_ratio)
            return;
        this._aspect_ratio = aspect_ratio;
        this.projection_left = -this._projection.top * this._aspect_ratio;
        this.projection_right = this._projection.top * this._aspect_ratio;
        // This is resetting the inertia. We do this selectively here (not using reset_inertia())
        // update_aspect_ratio is called when the camera is pushed.
        // When the scene_bounds change, we want to recalculate the maxbounds and set our projection.far accordingly. We then also update the _target_position.
        // The _target_position is set to _position when resetting intertia (reset_inertia()), so we avoid that here as to not void our blend_target()
        this._target_projection_left = this._projection.left;
        this._target_projection_right = this._projection.right;
    }
    get_projection_matrix(_custom_aspect_ratio) {
        if (_custom_aspect_ratio === undefined) {
            return this.projection.get_projection_matrix();
        }
        else {
            const projection = this.projection.clone();
            projection.left = -this.projection.top * _custom_aspect_ratio;
            projection.right = this.projection.top * _custom_aspect_ratio;
            return projection.get_projection_matrix();
        }
    }
    set projection_left(val) {
        if (this._projection.left !== val) {
            this._projection.left = val;
            this.is_dirty = true;
        }
    }
    set projection_right(val) {
        if (this._projection.right !== val) {
            this._projection.right = val;
            this.is_dirty = true;
        }
    }
    set projection_top(val) {
        if (this._projection.top !== val) {
            this._projection.top = val;
            this.is_dirty = true;
        }
    }
    set projection_bottom(val) {
        if (this._projection.bottom !== val) {
            this._projection.bottom = val;
            this.is_dirty = true;
        }
    }
    set position_internal(val) {
        if (val.x !== this._position_on_plane.x || val.y !== this._position_on_plane.y) {
            IVector2.set(this._position_on_plane, val.x, val.y);
            this.plane_position_change_observable.notify(this, IVector2.copy(this._position_on_plane)); // For updating debug geometry, and similar.
            this.is_dirty = true;
        }
    }
    /**
    * Set the position on the view plane given x and y coordinate.
    * Clears any limits that have been set provided that the values changed.
    */
    set position_on_plane(val) {
        this.position_internal = { x: val.x, y: val.y };
        this.clear_all_limits();
        this.reset_inertia();
    }
    get position_on_plane() {
        return this._position_on_plane;
    }
    /**
     * Sets the size of the orthographic view in world units (meters)
     * Sets the height of the view. The width will be scaled with the aspect ratio.
     * The size must be larger than zero.
     * Clears any limits that have been set.
     */
    set size(val) {
        const size = clamp(val, MIN_FRUSTUM_VALUE, Number.POSITIVE_INFINITY);
        this.projection_left = -size * 0.5 * this._aspect_ratio;
        this.projection_right = size * 0.5 * this._aspect_ratio;
        this.projection_top = size * 0.5;
        this.projection_bottom = -size * 0.5;
        this.clear_all_limits();
        this.reset_inertia();
    }
    /**
     * Sets the direction from which the camera faces the scene using a {@link CardinalDirection}
     * Clears any limits that have been set.
     * @param cardinal_direction
     */
    set_direction(cardinal_direction) {
        this.set_cardinal_direction_internal(cardinal_direction);
        this.reset_inertia();
        this.clear_all_limits();
    }
    set_cardinal_direction_internal(cardinal_direction) {
        switch (cardinal_direction) {
            case CardinalDirection.TOP:
                IVector3.copy_to_ref(ViewerCamera.RIGHT, this._right);
                IVector3.copy_to_ref(ViewerCamera.FORWARD, this._up);
                break;
            case CardinalDirection.BOTTOM:
                IVector3.copy_to_ref(ViewerCamera.RIGHT, this._right);
                IVector3.copy_to_ref(IVector3.scale(ViewerCamera.FORWARD, -1), this._up);
                break;
            case CardinalDirection.LEFT:
                IVector3.copy_to_ref(IVector3.scale(ViewerCamera.FORWARD, -1), this._right);
                IVector3.copy_to_ref(ViewerCamera.UP, this._up);
                break;
            case CardinalDirection.RIGHT:
                IVector3.copy_to_ref(ViewerCamera.FORWARD, this._right);
                IVector3.copy_to_ref(ViewerCamera.UP, this._up);
                break;
            case CardinalDirection.FRONT:
                IVector3.copy_to_ref(ViewerCamera.RIGHT, this._right);
                IVector3.copy_to_ref(ViewerCamera.UP, this._up);
                break;
            case CardinalDirection.BACK:
                IVector3.copy_to_ref(IVector3.scale(ViewerCamera.RIGHT, -1), this._right);
                IVector3.copy_to_ref(ViewerCamera.UP, this._up);
                break;
        }
        this.is_dirty = true;
    }
    /**
     * Will change the camera position on the view plane so that the given world space position is in the center of the camera.
     * This has no effect on camera direction and the center_position can be "behind" the camera.
     * Clears any limits that have been set!
     * @param center_position A world space coordinate
     */
    center_camera_on_position(center_position) {
        this.position_internal = {
            x: IVector3.dot(this._right, center_position),
            y: IVector3.dot(this._up, center_position)
        };
        this.reset_inertia();
        this.clear_all_limits();
    }
    /**
     * Set a custom view plane. If you want to use cardinal directions, just use {@link set_direction} instead.
     * Clears any limits that have been set.
     * @param right the intended right direction, perpendicular to up.
     * @param up the intended up direction, perpendicular to right.
     */
    set_view_plane(right, up) {
        IVector3.copy_to_ref(IVector3.normalize_safe(right), this._right);
        IVector3.copy_to_ref(IVector3.normalize_safe(up), this._up);
        this.clear_all_limits();
    }
    get projection() {
        const scene_bounds = this.viewer.get_scene_bounds();
        if (!IBoundingBox.equals(this._scene_bounds, scene_bounds)) {
            this.is_dirty = true;
            this._scene_bounds = new IBoundingBox(scene_bounds.min, scene_bounds.max);
        }
        this.update_camera_transform_cache();
        return this._projection;
    }
    get blend_target() {
        // This should be the point in space where the look vector intersects the plane with -look as normal intersecting the would bounds center
        const scene_bounds = this.viewer.get_scene_bounds();
        const bounds_center = IVector3.scale_in_place(IVector3.add(scene_bounds.min, scene_bounds.max), 0.5);
        const distance = IVector3.dot(IVector3.subtract(bounds_center, this.position), this.forward);
        return IVector3.add(this.position, IVector3.scale(this.forward, distance));
    }
    /**
     * Flag indicating if any of the parameters have changed since last generation of view matrix
     */
    get is_dirty() {
        return this._is_dirty;
    }
    set is_dirty(val) {
        this._is_dirty = val;
    }
    /**
     * Called whenever InputManager decided that the camera is the receiver of a Drag action
     * @param evt Event with the drag info
     */
    drag_input(evt) {
        // We consider a drag for Orthographic camera to be a pan action
        this.pan_input(evt);
    }
    pan_input(evt) {
        this._pan_input.x += -evt.delta_x * this._pan_sensitivity_x;
        this._pan_input.y += evt.delta_y * this._pan_sensitivity_y;
    }
    /**
     * Called whenever the InputManager decided that the camera is the receiver of a zoom action (scroll or pinch)
     * @param evt Event with the zoom info
     */
    zoom_input(evt) {
        const target_projection_offset = evt.delta * 0.01;
        const left = this._target_projection_left - target_projection_offset * this._aspect_ratio;
        const right = this._target_projection_right + target_projection_offset * this._aspect_ratio;
        const top = this._target_projection_top + target_projection_offset;
        const bottom = this._target_projection_bottom - target_projection_offset;
        this._target_projection_left = clamp(left, -this._offset_max * this._aspect_ratio, -this._offset_min * this._aspect_ratio);
        this._target_projection_right = clamp(right, this._offset_min * this._aspect_ratio, this._offset_max * this._aspect_ratio);
        this._target_projection_top = clamp(top, this._offset_min, this._offset_max);
        this._target_projection_bottom = clamp(bottom, -this._offset_max, -this._offset_min);
    }
    get camera_transform() {
        this.update_camera_transform_cache();
        return this._camera_transform_cache;
    }
    get view_matrix() {
        this.update_camera_transform_cache();
        return this._view_matrix_cache;
    }
    update_camera_transform_cache() {
        if (!this.is_dirty)
            return;
        IVector3.copy_to_ref(this._right, this._camera_transform_cache[0]);
        IVector3.copy_to_ref(this._up, this._camera_transform_cache[1]);
        IVector3.cross_to_ref(this._up, this._right, this._camera_transform_cache[2]);
        IVector3.set(this._camera_transform_cache[3], 0, 0, 0);
        IMatrix4.copy_to_ref(IMatrix4.transpose(this._camera_transform_cache), this._view_matrix_cache);
        const bounds_in_camera_space = ITransformedBoundingBox.get_encapsulating_axis_aligned_bounding_box(new ITransformedBoundingBox(this._scene_bounds, this._view_matrix_cache));
        // Add some margin to our bounds to give room for numerical errors. Arbitrarily chosen value.
        const bounds_depth_epsilon = 0.1;
        bounds_in_camera_space.min.z -= bounds_depth_epsilon;
        bounds_in_camera_space.max.z += bounds_depth_epsilon;
        this._projection.near = 0.0;
        this._projection.far = bounds_in_camera_space.max.z - bounds_in_camera_space.min.z;
        IVector3.set(this._view_matrix_cache[3], -this._position_on_plane.x, -this._position_on_plane.y, -bounds_in_camera_space.min.z);
        IMatrix4.orthonormal_rotation_affine_transform_inverse_to_ref(this._view_matrix_cache, this._camera_transform_cache);
        this._notify_observers = true;
        this.is_dirty = false;
    }
    on_activation() {
        if (!this._scene_bounds_changed_observer) {
            this._scene_bounds_changed_observer = this.viewer.get_scene_bounds_changed_observable().add(this.on_scene_bounds_changed);
        }
        // The scene bounds might have changed since we created the camera
        this._scene_bounds = this.viewer.get_scene_bounds();
        this.is_dirty = true;
    }
    on_deactivation() {
        if (this._scene_bounds_changed_observer) {
            this.viewer.get_scene_bounds_changed_observable().remove(this._scene_bounds_changed_observer);
            this._scene_bounds_changed_observer = null;
        }
    }
    update(delta_time) {
        var _a;
        // We only want to debounce/lerp once per frame even if we are the active camera for multiple views
        const delta_time_in_seconds = this._previous_update_frame !== delta_time.elapsed_frames ? delta_time.seconds : 0;
        this._previous_update_frame = delta_time.elapsed_frames;
        this._target_position_on_plane.x = this.integrate_springy_constrained_input(this._target_position_on_plane.x, this._pan_input.x, LIMIT_SPRING_STIFFNESS_PAN, this._pan_min.x, this._pan_max.x);
        this._target_position_on_plane.y = this.integrate_springy_constrained_input(this._target_position_on_plane.y, this._pan_input.y, LIMIT_SPRING_STIFFNESS_PAN, this._pan_min.y, this._pan_max.y);
        IVector2.set(this._pan_input, 0, 0);
        if (Math.abs(this._target_projection_left - this._projection.left) > EPSILON_DISTANCE) {
            this.projection_left = lerp(this._projection.left, this._target_projection_left, Math.min(1.0, delta_time_in_seconds * INERTIA_SPEED_ZOOM));
        }
        else {
            this.projection_left = this._target_projection_left;
        }
        if (Math.abs(this._target_projection_right - this._projection.right) > EPSILON_DISTANCE) {
            this.projection_right = lerp(this._projection.right, this._target_projection_right, Math.min(1.0, delta_time_in_seconds * INERTIA_SPEED_ZOOM));
        }
        else {
            this.projection_right = this._target_projection_right;
        }
        if (Math.abs(this._target_projection_top - this._projection.top) > EPSILON_DISTANCE) {
            this.projection_top = lerp(this._projection.top, this._target_projection_top, Math.min(1.0, delta_time_in_seconds * INERTIA_SPEED_ZOOM));
        }
        else {
            this.projection_top = this._target_projection_top;
        }
        if (Math.abs(this._target_projection_bottom - this._projection.bottom) > EPSILON_DISTANCE) {
            this.projection_bottom = lerp(this._projection.bottom, this._target_projection_bottom, Math.min(1.0, delta_time_in_seconds * INERTIA_SPEED_ZOOM));
        }
        else {
            this.projection_bottom = this._target_projection_bottom;
        }
        const pivot_lerp_t = Math.min(1.0, delta_time_in_seconds * LIMIT_SPRING_LERP_MULTIPLIER * INERTIA_SPEED_PAN);
        if (this._pan_min.x === this._pan_max.x) {
            this._position_on_plane.x = this._target_position_on_plane.x;
        }
        else if (!this._is_panning) {
            this._target_position_on_plane.x = target_lerp(this._target_position_on_plane.x, this.limit_offset_x(this._target_position_on_plane.x), pivot_lerp_t, EPSILON_DISTANCE);
        }
        if (this._pan_min.y === this._pan_max.y) {
            this._position_on_plane.y = this._target_position_on_plane.y;
        }
        else if (!this._is_panning) {
            this._target_position_on_plane.y = target_lerp(this._target_position_on_plane.y, this.limit_offset_y(this._target_position_on_plane.y), pivot_lerp_t, EPSILON_DISTANCE);
        }
        this.position_internal = new IVector2(target_lerp(this._position_on_plane.x, this._target_position_on_plane.x, Math.min(1.0, delta_time_in_seconds * INERTIA_SPEED_PAN), EPSILON_DISTANCE), this._position_on_plane.y = target_lerp(this._position_on_plane.y, this._target_position_on_plane.y, Math.min(1.0, delta_time_in_seconds * INERTIA_SPEED_PAN), EPSILON_DISTANCE));
        if (this._notify_observers) {
            this.update_observable.notify(this);
            this._notify_observers = false;
        }
        (_a = this._debug_geometry) === null || _a === void 0 ? void 0 : _a.update();
    }
    get debug_geometry() {
        return this._debug_geometry;
    }
    create_debug_geometry() {
        if (this._debug_geometry)
            return;
        this._debug_geometry = create_visualizers_container({
            frustum: new CameraFrustumVisualizer(this, this.viewer, undefined, 0.01, LineType.DASHED),
            camera: new OrthographicCameraVisualizer(this, this.viewer)
        }, this.viewer, true);
    }
    remove_debug_geometry() {
        var _a;
        (_a = this._debug_geometry) === null || _a === void 0 ? void 0 : _a.dispose();
        this._debug_geometry = null;
    }
    update_debug_geometry() {
        if (this.debug_geometry) {
            this.debug_geometry.update();
        }
    }
    /**
     * Clones the camera to create an exact duplicate.
     * @returns A new Camera instance that is an exact copy of the original camera.
     */
    clone() {
        const { x, y } = this._position_on_plane;
        const clone = new OrthographicPlannerCamera_1(CardinalDirection.FRONT, { x: x, y: y }, this.viewer);
        clone.set_view_plane(this.right, this.up);
        clone._projection = this._projection.clone();
        // Limits
        IVector2.copy_to_ref(this._pan_min, clone._pan_min);
        IVector2.copy_to_ref(this._pan_max, clone._pan_max);
        clone._offset_max = this._offset_max;
        clone._offset_min = this._offset_min;
        return clone;
    }
    reset_inertia() {
        IVector2.copy_to_ref(this._position_on_plane, this._target_position_on_plane);
        this._target_projection_left = this.projection.left;
        this._target_projection_right = this.projection.right;
        this._target_projection_top = this.projection.top;
        this._target_projection_bottom = this.projection.bottom;
    }
    set_zoom_limits(limits) {
        this.set_zoom_limits_internal(limits);
        if (this.is_dirty) {
            this.reset_inertia();
        }
    }
    set_zoom_limits_internal(limits) {
        limits = update_from_partial({
            near: DEFAULT_OFFSET.min,
            far: DEFAULT_OFFSET.max
        }, limits);
        this._offset_min = Math.max(limits.near, ALLOWED_OFFSET.min);
        this._offset_max = Math.min(limits.far, ALLOWED_OFFSET.max);
        this.projection_left = clamp(this.projection.left, -this._offset_max * this._aspect_ratio, -this._offset_min * this._aspect_ratio);
        this.projection_right = clamp(this.projection.right, this._offset_min * this._aspect_ratio, this._offset_max * this._aspect_ratio);
        this.projection_top = clamp(this.projection.top, this._offset_min, this._offset_max);
        this.projection_bottom = clamp(this.projection.bottom, -this._offset_max, -this._offset_min);
    }
    set_pan_limits(limits) {
        this.set_pan_limits_internal(limits);
        this.position_internal = { x: this.limit_offset_x(this.position_on_plane.x), y: this.limit_offset_y(this.position_on_plane.y) };
        if (this.is_dirty) {
            this.reset_inertia();
        }
    }
    set_pan_limits_internal(limits) {
        IVector2.set(this._pan_min, limits.min.x, limits.min.y);
        IVector2.set(this._pan_max, limits.max.x, limits.max.y);
    }
    /**
     * Frames the bounds specified as snuggly as possible.
     * Clears any limits that have been set.
     * @param bounds The bounds that you want to frame
     */
    frame_precise(bounds, aspect_ratio) {
        const center = IVector3.scale_in_place(IVector3.add(bounds.min, bounds.max), 0.5);
        const position = IVector3.subtract(center, this.forward);
        const camera_matrix = IMatrix4.look_at(position, center, this._up);
        const view_matrix = IMatrix4.orthonormal_rotation_affine_transform_inverse(camera_matrix);
        const bounds_in_camera_space = ITransformedBoundingBox.get_encapsulating_axis_aligned_bounding_box(new ITransformedBoundingBox(this._scene_bounds, view_matrix));
        const max_abs_x = Math.abs(bounds_in_camera_space.max.x);
        const max_abs_y = Math.abs(bounds_in_camera_space.max.y);
        const offset_x = IVector3.multiply(this._right, center);
        const offset_y = IVector3.multiply(this._up, center);
        this.position_internal = { x: offset_x.x + offset_x.y + offset_x.z, y: offset_y.x + offset_y.y + offset_y.z };
        const max = max_abs_x > max_abs_y ? max_abs_x : max_abs_y;
        if (aspect_ratio === undefined) {
            aspect_ratio = this._aspect_ratio;
        }
        this.projection_left = aspect_ratio >= 1 ? -max * aspect_ratio : -max;
        this.projection_right = aspect_ratio >= 1 ? max * aspect_ratio : max;
        this.projection_top = aspect_ratio >= 1 ? max : max / aspect_ratio;
        this.projection_bottom = aspect_ratio >= 1 ? -max : -max / aspect_ratio;
        this.reset_inertia();
        this.clear_all_limits();
    }
    limit_offset_x(offset_x) {
        return clamp(offset_x, this._pan_min.x, this._pan_max.x);
    }
    limit_offset_y(offset_y) {
        return clamp(offset_y, this._pan_min.y, this._pan_max.y);
    }
    clear_all_limits() {
        this.set_pan_limits_internal({
            min: { x: Number.NEGATIVE_INFINITY, y: Number.NEGATIVE_INFINITY },
            max: { x: Number.POSITIVE_INFINITY, y: Number.POSITIVE_INFINITY }
        });
        this.set_zoom_limits_internal({ near: ALLOWED_OFFSET.min, far: ALLOWED_OFFSET.max });
    }
    calculate_position_from_plane_coordinates_to_ref(x, y, z, ref) {
        // Poor mans matrix multiplication
        ref.x = this.right.x * x + this.up.x * y + this.forward.x * z;
        ref.y = this.right.y * x + this.up.y * y + this.forward.y * z;
        ref.z = this.right.z * x + this.up.z * y + this.forward.z * z;
        return ref;
    }
    generate_debug_geometries() {
        /**
         * 1 __ 2
         *  |  |
         * 0 __ 3
         * The following list has the corners depicted in this cube
         */
        let corners = [
            { x: this._pan_min.x, y: this._pan_min.y },
            { x: this._pan_min.x, y: this._pan_max.y },
            { x: this._pan_max.x, y: this._pan_max.y },
            { x: this._pan_max.x, y: this._pan_min.y }
        ];
        /**
         * All required lines are
         * 0->1, 0->3,
         * 2->1, 2->3
         */
        const indices = [
            0, 1, 0, 3,
            2, 1, 2, 3
        ];
        const corner_z = 0.0;
        const ui_lines = [];
        const color = { r: 1, g: 0.2, b: 0.2 };
        for (let i = 0; i < indices.length; i += 2) {
            const corner1 = corners[indices[i + 0]];
            const corner2 = corners[indices[i + 1]];
            const corner1_position = this.calculate_position_from_plane_coordinates_to_ref(corner1.x, corner1.y, corner_z, new IVector3());
            const corner2_position = this.calculate_position_from_plane_coordinates_to_ref(corner2.x, corner2.y, corner_z, new IVector3());
            const ui_line_id = this.viewer.create_ui_line(LineType.SOLID, corner1_position, corner2_position, color, 0.01);
            ui_lines.push(ui_line_id);
            // Don't render it if we're NaN
            if (isNaN(IVector3.dot(corner1_position, corner2_position))) {
                this.viewer.set_ui_line_visible(ui_line_id, false);
            }
        }
        const primitive_builder = new PrimitiveBuilder(this.viewer);
        const target_sphere = primitive_builder.create_sphere(0.06, { segments: 64 });
        this.update_observable.add(() => {
            corners = [
                { x: this._pan_min.x, y: this._pan_min.y },
                { x: this._pan_min.x, y: this._pan_max.y },
                { x: this._pan_max.x, y: this._pan_max.y },
                { x: this._pan_max.x, y: this._pan_min.y }
            ];
            for (let i = 0, line_index = 0; i < indices.length; i += 2, line_index++) {
                const corner1 = corners[indices[i + 0]];
                const corner2 = corners[indices[i + 1]];
                const corner1_position = this.calculate_position_from_plane_coordinates_to_ref(corner1.x, corner1.y, corner_z, new IVector3());
                const corner2_position = this.calculate_position_from_plane_coordinates_to_ref(corner2.x, corner2.y, corner_z, new IVector3());
                if (isNaN(IVector3.dot(corner1_position, corner2_position))) {
                    this.viewer.set_ui_line_visible(ui_lines[line_index], false);
                }
                else {
                    this.viewer.set_ui_line_visible(ui_lines[line_index], true);
                    this.viewer.update_ui_line(ui_lines[line_index], corner1_position, corner2_position);
                }
            }
        });
        this.plane_position_change_observable.add((sender, position) => {
            const target_position = this.calculate_position_from_plane_coordinates_to_ref(position.x, position.y, corner_z, new IVector3());
            this.viewer.set_position(target_sphere, target_position.x, target_position.y, target_position.z);
        });
    }
    dispose_impl() {
        if (this._scene_bounds_changed_observer) {
            this.viewer.get_scene_bounds_changed_observable().remove(this._scene_bounds_changed_observer);
            this._scene_bounds_changed_observer = null;
        }
    }
    /**
     * Helper function to integrate input gathered over a frame, with springy limits.
     * @param x The state of the input
     * @param delta The input delta gathered during the frame
     * @param stiffness The stiffness of the "spring" beyond the limits
     * @param limit_min The limit minimum
     * @param limit_max The limit maximum
     * @returns The new input's state value
     */
    integrate_springy_constrained_input(x, delta, stiffness, limit_min, limit_max) {
        const limited_after_input = clamp(x + delta, limit_min, limit_max);
        if (limited_after_input === x + delta) {
            return x + delta;
        }
        if ((limited_after_input - (x + delta)) * delta >= 0.0) {
            // The input is going away from the limit, so don't limit the sensitivity.
            return x + delta;
        }
        else if (clamp(x, limit_min, limit_max) !== x) {
            // We're outside of our limited range and we're moving further away
            if (x >= limit_max) {
                return x - delta * Math.exp(stiffness * (limit_max - x)) / -stiffness;
            }
            else {
                return x + delta * Math.exp(stiffness * (x - limit_min)) / stiffness;
            }
        }
        else {
            // We're not limited yet, but might be
            // Add the input like normal up until the point we hit the limit
            const input_used_up = Math.sign(delta) * Math.abs(limited_after_input - clamp(x, limit_min, limit_max));
            const input_left_after_limit = delta - input_used_up;
            // Update state
            x = limited_after_input;
            delta = input_left_after_limit;
            // Integrate the input with the springiness for the portion after the limit was exceeded.
            if (x >= limit_max) {
                return x - delta * Math.exp(stiffness * (limit_max - x)) / -stiffness;
            }
            else {
                return x + delta * Math.exp(stiffness * (x - limit_min)) / stiffness;
            }
        }
    }
};
__decorate([
    Deprecated({ new: 'create_debug_geometry' })
], OrthographicPlannerCamera.prototype, "generate_debug_geometries", null);
OrthographicPlannerCamera = OrthographicPlannerCamera_1 = __decorate([
    Debug.Describe(Object.assign(Object.assign({}, ORTHOGRAPHIC_CAMERA_DESCRIPTION), { name: 'orthographic-planner-camera-description' }))
], OrthographicPlannerCamera);
export { OrthographicPlannerCamera };
//# sourceMappingURL=OrthographicPlannerCamera.js.map